import {DOCUMENT_TAG} from 'app/documents/api';
import {LANE_RTK_TAGS} from 'app/lanes/api';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  getPolicyDetailURLByHashid,
  POLICIES_API,
  POLICIES_API_URL_COPY,
  POLICIES_VERSIONS_API,
  POLICIES_VERSIONS_APPROVE_API_URL,
  POLICY_PROCEDURE_ATTACH_API,
  POLICY_PROCESS_ATTACH_API,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import type {
  PoliciesFetchParamsType,
  PoliciesLocationParamsType,
} from './types';
import type {CreateNewDocProps} from 'app/general/types';
import type {
  DetailPolicySerializedType,
  DetailPolicyVersionSerializedType,
  ListPolicyVersionSerializedType,
  ListResponseType,
} from 'sweet-types';

export const POLICY_RTK_TAGS = {
  TAGS: 'POLICY',
  HASHID: 'POLICYHASHID',
  VERSIONS: 'POLICYVERSION',
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      POLICY_RTK_TAGS.TAGS,
      POLICY_RTK_TAGS.HASHID,
      POLICY_RTK_TAGS.VERSIONS,
      DOCUMENT_TAG,
      LANE_RTK_TAGS.TAGS,
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getPolicyByHashid: build.query<DetailPolicySerializedType, string>({
        query: (hashid) => ({
          url: getPolicyDetailURLByHashid(hashid),
        }),
        providesTags: (response) =>
          response != null
            ? [{type: POLICY_RTK_TAGS.HASHID, id: response.url}]
            : [],

        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the policy.'),
        }),
      }),

      getPolicies: build.query<
        ListResponseType<DetailPolicySerializedType>,
        PoliciesFetchParamsType
      >({
        query: (params) => ({
          url: POLICIES_API,
          params,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Unable to load the policies.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
        providesTags: providesTagsByURL(POLICY_RTK_TAGS.TAGS),
      }),

      updatePolicy: build.mutation<
        DetailPolicySerializedType,
        {url: string} & Partial<DetailPolicySerializedType> & {
            successMessage?: string;
            errorMessage?: string;
          }
      >({
        query: ({url, ...body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: ({successMessage}) => successMessage ?? null,
          onError: (error, {errorMessage}) =>
            errorMessage ?? gt('Unable to update the policy'),
        }),
        invalidatesTags: (result, error, {url}) => [
          {type: POLICY_RTK_TAGS.TAGS, id: url},
          {type: POLICY_RTK_TAGS.HASHID, id: url},
        ],
      }),

      createPolicy: build.mutation<
        DetailPolicySerializedType,
        CreateNewDocProps
      >({
        query: (data) => ({
          url: POLICIES_API,
          method: 'POST',
          body: data,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not create the policy.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
        ],
      }),

      attachPolicyToProcess: build.mutation<
        unknown,
        {
          genericLinks: Array<{policy: string; process: string}>;
        }
      >({
        query: ({genericLinks}) => ({
          url: POLICY_PROCESS_ATTACH_API,
          method: 'POST',
          body: genericLinks,
        }),
        onQueryStarted: (args, api) =>
          showMessages({
            onSuccess: () => {
              const ct = getContentType('proceduregroup');
              return gt(
                'Policy attached to %s',
                args.genericLinks.length === 1 ? ct.name : ct.pluralName
              );
            },
            onError: () => {
              const ct = getContentType('proceduregroup');
              return gt(
                'Unable to attach policy to %s',
                args.genericLinks.length === 1 ? ct.name : ct.pluralName
              );
            },
          })(args, api),
      }),

      detachPolicyFromProcess: build.mutation<Record<string, never>, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => {
            const ct = getContentType('proceduregroup');
            return gt('Policy removed from %s', ct.name);
          },
          onError: () => {
            const ct = getContentType('proceduregroup');
            return gt('Unable to remove policy from %s', ct.name);
          },
        }),
      }),

      attachPolicyToProcedure: build.mutation<
        unknown,
        {
          genericLinks: Array<{policy: string; procedure: string}>;
        }
      >({
        query: ({genericLinks}) => ({
          url: POLICY_PROCEDURE_ATTACH_API,
          method: 'POST',
          body: genericLinks,
        }),
        onQueryStarted: (args, api) =>
          showMessages({
            onSuccess: () => {
              const ct = getContentType('procedure');
              return gt(
                'Policy attached to %s',
                args.genericLinks.length === 1 ? ct.name : ct.pluralName
              );
            },
            onError: () => {
              const ct = getContentType('procedure');
              return gt(
                'Unable to attach policy to %s',
                args.genericLinks.length === 1 ? ct.name : ct.pluralName
              );
            },
          })(args, api),
      }),

      detachPolicyFromProcedure: build.mutation<Record<string, never>, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: () => {
            const ct = getContentType('procedure');
            return gt('Policy removed from %s', ct.name);
          },
          onError: () => {
            const ct = getContentType('procedure');
            return gt('Unable to remove policy from %s', ct.name);
          },
        }),
      }),

      approvePolicyVersion: build.mutation<
        unknown,
        {
          versionURL: string;
          documentId: number;
          options: {notify?: boolean; signoff?: boolean};
        }
      >({
        query: ({versionURL, options}) => ({
          method: 'POST',
          url: POLICIES_VERSIONS_APPROVE_API_URL,
          body: {url: versionURL, ...options},
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({options}) =>
              options.notify
                ? gt('Approved. Teammates will be notified')
                : gt('This version is now approved and live.'),
            onError: () => gt('Could not approve that version.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: LANE_RTK_TAGS.TAGS, id: 'list'},
          {type: POLICY_RTK_TAGS.TAGS, id: 'list'},
          {type: POLICY_RTK_TAGS.HASHID, id: 'list'},
        ],
      }),

      duplicatePolicy: build.mutation<
        DetailPolicySerializedType,
        {name: string; url: string; teams?: Array<string>}
      >({
        query: (body) => ({
          url: POLICIES_API_URL_COPY,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to duplicate'),
          })(args, api);
        },
        invalidatesTags: () => [{type: POLICY_RTK_TAGS.TAGS, id: 'list'}],
      }),

      policyVersionById: build.query<
        DetailPolicyVersionSerializedType,
        number | string
      >({
        query: (id) => ({
          url: `${POLICIES_VERSIONS_API}${id}/`,
        }),
        providesTags: (response) =>
          response != null
            ? [{type: POLICY_RTK_TAGS.VERSIONS, id: response.url}]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the version.'),
        }),
      }),

      policyVersions: build.query<
        ListResponseType<ListPolicyVersionSerializedType>,
        {
          objectId?: string | number;
          page?: string | number;
          page_size?: string;
        }
      >({
        query: ({objectId, page = 1, page_size = DEFAULT_PAGE_SIZE}) => ({
          url: POLICIES_VERSIONS_API,
          params: {version_object_id: objectId, page, page_size},
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to load the policy versions.'),
          })(args, api);
        },
        providesTags: providesTagsByURL(POLICY_RTK_TAGS.VERSIONS),
      }),

      updatePolicyVersion: build.mutation<
        DetailPolicyVersionSerializedType,
        {url: string} & Partial<DetailPolicyVersionSerializedType> & {
            successMessage?: string;
            errorMessage?: string;
          }
      >({
        query: ({url, ...body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onSuccess: ({successMessage}) => successMessage ?? null,
          onError: (error, {errorMessage}) =>
            errorMessage ?? gt('Unable to update the policy'),
        }),
        invalidatesTags: (result, error, {url}) => [
          {type: POLICY_RTK_TAGS.VERSIONS, id: url},
        ],
      }),
    }),
  });

export const {
  useGetPoliciesQuery: usePolicies,
  useLazyGetPoliciesQuery: useLazyPolicies,
  useUpdatePolicyMutation: useUpdatePolicy,
  useCreatePolicyMutation: useCreatePolicy,
  useGetPolicyByHashidQuery: usePolicyByHashid,
  usePolicyVersionByIdQuery: usePolicyVersionById,
  usePolicyVersionsQuery: usePolicyVersions,
  useUpdatePolicyVersionMutation: useUpdatePolicyVersion,
  useAttachPolicyToProcessMutation: useAttachPolicyToProcess,
  useDetachPolicyFromProcessMutation: useDetachPolicyFromProcess,
  useAttachPolicyToProcedureMutation: useAttachPolicyToProcedure,
  useDetachPolicyFromProcedureMutation: useDetachPolicyFromProcedure,
  useApprovePolicyVersionMutation: useApprovePolicyVersion,
  useDuplicatePolicyMutation: useDuplicatePolicy,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: PoliciesLocationParamsType
): PoliciesFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];

  return {
    ...query,
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    page: query.page != null ? query.page : '1',
    page_size:
      query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE.toString(),
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
  };
};
